import * as storage from 'src/plugins/helpers/storage'
import { get } from 'lodash'
import { setRouterType } from 'src/router/composables/useRolesSystem'
import { isImpersonating, isAdminImpersonating } from 'src/utils'

const ALLOWED_REDIRECT_DOMAINS = ['aloware.com', 'aloware.io', 'alodev.org', 'alostaging.com', 'aloreseller.com']

// Token presence at page load, evaluated at import — before any 401 handler or
// clear() can remove it. getCookieUser() runs on every boot for cross-domain
// sessions, and only the run that establishes auth where there was none is a
// login; reading storage live would call a plain expiry a login, since a failed
// cold-boot auth clears the token before the redirect to /login.
//
// Safe as a snapshot only because every caller is boot-scoped (App.vue mounted(),
// Login.vue created()). A mid-session caller — re-checking the cookie on window
// focus, say — would read a stale answer and need the live value passed in.
const hadApiTokenAtPageLoad = storage.local.getItem('api_token') !== null

export const isAllowedRedirectSource = (url) => {
  try {
    const { hostname, protocol } = new URL(url)

    if (protocol !== 'https:' && protocol !== 'http:') {
      return false
    }

    return ALLOWED_REDIRECT_DOMAINS.some(domain => hostname === domain || hostname.endsWith('.' + domain))
  } catch {
    return false
  }
}

/**
 * Resolve a user-supplied redirect/return value to a destination that is safe
 * to navigate to, guarding against open-redirect attacks (CWE-601).
 *
 * The candidate is resolved against the current origin with the URL parser,
 * which normalizes the same tricks the browser normalizes on navigation
 * (backslashes, mixed/encoded slashes, protocol-relative URLs, userinfo). Only
 * the resolved result is trusted:
 *   - same-origin  -> return its path + query + hash (never an absolute URL), or
 *   - cross-origin -> allowed only when the host is an allowlisted Aloware host.
 *
 * Anything else resolves to `fallback`.
 *
 * `rawReturn` is expected to be already URL-decoded (callers read it via
 * vue-router's `$route.query`, which decodes once); the `URL` constructor
 * handles any remaining normalization, so we must not decode again here — a
 * second decode corrupts legitimate paths that contain a literal `%`.
 *
 * @param {?string} rawReturn already-decoded redirect value
 * @param {?string} fallback destination when `rawReturn` is missing/unsafe
 * @returns {?string}
 */
export const safeReturnPath = (rawReturn, fallback = '/login') => {
  if (!rawReturn) {
    return fallback
  }

  try {
    const resolved = new URL(rawReturn, window.location.origin)

    if (resolved.protocol !== 'https:' && resolved.protocol !== 'http:') {
      return fallback
    }

    if (resolved.origin === window.location.origin) {
      return resolved.pathname + resolved.search + resolved.hash
    }

    if (isAllowedRedirectSource(resolved.href)) {
      return resolved.href
    }

    return fallback
  } catch {
    return fallback
  }
}

const check = async (authModule, payload, skipSetAuthenticated) => {
  const { commit } = authModule
  const preventLogout = get(payload, 'preventLogout', false)

  try {
    if (storage.local.getItem('api_token') === null) {
      return Promise.reject('unauthorized')
    }

    window.axios.defaults.headers.common['Authorization'] = 'Bearer ' + storage.local.getItem('api_token')

    commit('SET_LOADING', true)

    const response = await window.axios.post('/get-auth-user', {
      device_info: null
    })

    // The server returns the user's current api_token. Adopt it when it has
    // rotated (suspend/unsuspend mints a new one) — otherwise an alive web
    // session keeps this endpoint green while every auth:api call rejects the
    // stale bearer, leaving the app in a permanent 401 state.
    const freshToken = response?.data?.user?.api_token
    if (freshToken && freshToken !== storage.local.getItem('api_token')) {
      storage.local.setItem('api_token', freshToken)
    }

    window.axios.defaults.headers.common['Authorization'] = 'Bearer ' + storage.local.getItem('api_token')

    if (!skipSetAuthenticated) {
      commit('SET_AUTHENTICATED', true)
    }

    const user = response.data.user
    if (user.settings) {
      user.settings.forEach(setting => {
        // Map setting name to user property
        const { name, value } = setting
        user[`setting_${name}`] = value
      })
      delete user.settings
    }

    commit('SET_PROFILE', user)
    setRouterType(authModule)
    commit('SET_LOADING', false)
    commit('SET_USAGE', user.usage, { root: true })
    commit('SET_USER_STATUS', user.enabled, { root: true })
    commit('SET_CURRENT_TIMEZONE', user.company.timezone, { root: true })

    return response
  } catch (err) {
    commit('SET_LOADING', false)
    if (!preventLogout) {
      // error
      commit('SET_AUTHENTICATED', false)
      commit('SET_PROFILE', null)
    }
    return Promise.reject(err)
  }
}

const login = async (authModule, {
  email,
  password,
  rememberMe,
  recaptchaResponse = null,
  isMobile = false,
  deviceInfo = null,
  requestedFrom = null,
  skipSetAuthenticated = false
}) => {
  const { commit } = authModule
  const config = {}

  const params = {
    email: email,
    password: password,
    remember_me: rememberMe,
    is_mobile: isMobile,
    device_info: deviceInfo
  }

  if (recaptchaResponse) {
    params.recaptcha_response = recaptchaResponse
  }

  if (requestedFrom) {
    config.headers = {
      'requested-from': requestedFrom
    }
  }

  try {
    commit('SET_LOADING', true)

    const response = await window.axios.post('/login', params, config)

    const {
      meta,
      data
    } = response.data

    storage.local.setItem('shared_cookie', meta.hashed_token)
    storage.local.setItem('api_token', meta.token)
    setSharedAuthCookie(meta.hashed_token)

    commit('SET_FIRST_LOGIN', data.first_login, { root: true })

    commit('SET_LOADING', false)

    await check(authModule, {}, skipSetAuthenticated)
    return response
  } catch (err) {
    commit('SET_LOADING', false)
    return Promise.reject(err)
  }
}

const getSharedCookie = () => {
  if (!window.location.hostname) {
    return storage.local.getItem('shared_cookie')
  }

  const name = 'aloware_shared_auth_token='
  const ca = document.cookie.split(';')
  const index = { i: 0 }
  const c = { data: null }
  for (index.i = 0; index.i < ca.length; index.i++) {
    c.data = ca[index.i]
    while (c.data.charAt(0) === ' ') {
      c.data = c.data.substring(1)
    }
    if (c.data.indexOf(name) === 0) {
      return c.data.substring(name.length, c.data.length)
    }
  }
  return null
}

const getCookieUser = async (authModule) => {
  const { commit } = authModule

  try {
    commit('SET_LOADING', true)

    const urlParams = new URLSearchParams(window.location.search)
    const impersonating = urlParams.get('impersonating')
    const source = urlParams.get('source')

    if (impersonating) {
      if (source && isAllowedRedirectSource(source)) {
        storage.local.setItem('impersonation_source', source)
      }
    }

    const cookieParams = { shared_token: getSharedCookie() }

    const response = await window.axios.get('/get-cookie-user', { params: cookieParams })

    const {
      meta,
      data
    } = response.data

    storage.local.setItem('api_token', meta.token)

    commit('SET_FIRST_LOGIN', data.first_login, { root: true })

    if (!hadApiTokenAtPageLoad) {
      commit('SET_JUST_LOGGED_IN', true, { root: true })
    }

    commit('SET_LOADING', false)

    await check(authModule, {})

    return response
  } catch (err) {
    commit('SET_LOADING', false)
    return Promise.reject(err)
  }
}

const logout = async ({ commit }) => {
  try {
    commit('SET_LOADING', true)

    const params = {
      is_impersonated: isImpersonating()
    }

    const response = await window.axios.post('/logout', params)

    clear({ commit })

    commit('SET_LOADING', false)

    return response
  } catch (err) {
    commit('SET_LOADING', false)
    return Promise.reject(err)
  }
}

const clearSharedAuthCookie = () => {
  if (!window.location.hostname) {
    return
  }
  const parts = window.location.hostname.split('.')
  if (parts.length > 2) {
    parts.shift()
  }
  const domain = '.' + parts.join('.')
  document.cookie = `aloware_shared_auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`
}

const setSharedAuthCookie = (token) => {
  if (!window.location.hostname) {
    return
  }
  const parts = window.location.hostname.split('.')
  if (parts.length > 2) {
    parts.shift()
  }
  const domain = '.' + parts.join('.')
  document.cookie = `aloware_shared_auth_token=${token}; path=/; domain=${domain}; SameSite=Lax`
}

const clear = ({ commit }) => {
  storage.local.removeItem('api_token')
  storage.local.removeItem('impersonation_source')
  storage.local.removeItem('portal_session')
  storage.local.removeItem('company_id')
  storage.local.removeItem('shared_cookie')

  clearSharedAuthCookie()

  window.axios.defaults.headers.common['Authorization'] = null

  if (window.Intercom) {
    window.Intercom('shutdown')
  }

  commit('SET_AUTHENTICATED', false)
  commit('SET_PROFILE', null)
}

const register = async ({ commit }, payload) => {
  try {
    const {
      name,
      email,
      password,
      companyName,
      phoneNumber,
      timezone
    } = payload
    const params = {
      name: name,
      email: email,
      password: password,
      company_name: companyName,
      phone_number: phoneNumber,
      timezone: timezone
    }
    commit('SET_LOADING', true)

    await window.axios.post('/register', params)

    commit('SET_LOADING', false)
  } catch (err) {
    commit('SET_LOADING', false)
    return Promise.reject(err)
  }
}

const forgotPass = async ({ commit }, {
  email,
  recaptchaResponse = null
}) => {
  try {
    const params = {
      email: email
    }

    if (recaptchaResponse) {
      params.recaptcha_response = recaptchaResponse
    }

    commit('SET_LOADING', true)
    await window.axios.post('/forgot', params)
    commit('SET_LOADING', false)
  } catch (err) {
    commit('SET_LOADING', false)
    return Promise.reject((err))
  }
}

const resetPass = async ({ commit }, payload) => {
  try {
    const {
      email,
      password,
      passwordConfirmation,
      token,
      recaptchaResponse = null
    } = payload

    const params = {
      email: email,
      password: password,
      password_confirmation: passwordConfirmation,
      token: token
    }

    if (recaptchaResponse) {
      params.recaptcha_response = recaptchaResponse
    }

    commit('SET_LOADING', true)
    await window.axios.post('/reset', params)
    commit('SET_LOADING', false)
  } catch (err) {
    commit('SET_LOADING', false)
    return Promise.reject(err)
  }
}

const impersonate = async ({ commit }, payload) => {
  try {
    const {
      userId,
      company
    } = payload

    commit('SET_LOADING', true)

    const response = await window.axios.post('/api/v1/user/' + userId + '/impersonate')

    const { apiToken } = response.data

    commit('SET_LOADING', false)

    if (company) {
      storage.local.setItem('api_token', apiToken)

      window.axios.defaults.headers.common['Authorization'] = 'Bearer ' + apiToken

      storage.local.setItem('company_id', company.id)

      return Promise.resolve()
    } else {
      return Promise.reject()
    }
  } catch (err) {
    commit('SET_LOADING', false)
    return Promise.reject(err)
  }
}

const setAgentStatus = ({ commit }, agentStatus) => {
  commit('SET_AGENT_STATUS', agentStatus)
}

const setProfile = ({ commit, state }, user) => {
  commit('SET_PROFILE', user)
  setRouterType({ commit, state })
}

const stopImpersonation = async (authModule) => {
  const { commit } = authModule
  try {
    const response = await window.axios.post('/stop-impersonation', {
      is_admin_impersonation: isAdminImpersonating()
    })
    clear({ commit })
    if (response.data.restored) {
      // User-to-user: restore original user's session in-place
      storage.local.setItem('api_token', response.data.api_token)
      window.axios.defaults.headers.common['Authorization'] = 'Bearer ' + response.data.api_token
      await check(authModule, {})
    }
    return response
  } catch (err) {
    commit('SET_LOADING', false)
    return Promise.reject(err)
  }
}

export default {
  check,
  login,
  getSharedCookie,
  getCookieUser,
  logout,
  clear,
  register,
  forgotPass,
  resetPass,
  impersonate,
  stopImpersonation,
  setAgentStatus,
  setProfile
}
